import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, MapPin, Building, Phone, Mail, Hash, X } from 'lucide-react';
import Select from 'react-select';
import api from '../../services/api';
import { toast } from 'react-toastify';
import styles from './SettingsPage.module.css';
import commonStyles from '../../styles/common.module.css';

const CustomerTerritoryList = () => {
  const [territories, setTerritories] = useState([]);
  const [formData, setFormData] = useState({
    customer_id: null, tb_number: '', tb_name: '', gosb_number: '', gosb_name: '',
    city_id: null, address: '', phone: '', email: ''
  });
  const [customers, setCustomers] = useState([]);
  const [cities, setCities] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTerritories();
    fetchCustomers();
    fetchCities();
  }, []);

  const fetchTerritories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/settings/customer-territories/');
      setTerritories(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки территорий');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/settings/customers/');
      setCustomers(response.data.map(c => ({ value: c.id, label: c.name })));
    } catch (error) {
      toast.error('Ошибка загрузки заказчиков');
    }
  };

  const fetchCities = async () => {
    try {
      const response = await api.get('/settings/cities/');
      setCities(response.data.map(city => ({ value: city.id, label: city.name })));
    } catch (error) {
      toast.error('Ошибка загрузки городов');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      customer_id: formData.customer_id?.value,
      city_id: formData.city_id?.value,
    };
    try {
      if (editingId) {
        await api.put(`/settings/customer-territories/${editingId}/`, data);
        toast.success('Территория обновлена');
      } else {
        await api.post('/settings/customer-territories/', data);
        toast.success('Территория создана');
      }
      closeModal();
      fetchTerritories();
    } catch (error) {
      toast.error('Ошибка сохранения территории');
    }
  };

  const handleEdit = (territory) => {
    setFormData({
      customer_id: { value: territory.customer_id, label: customers.find(c => c.value === territory.customer_id)?.label },
      tb_number: territory.tb_number || '',
      tb_name: territory.tb_name || '',
      gosb_number: territory.gosb_number || '',
      gosb_name: territory.gosb_name || '',
      city_id: { value: territory.city_id, label: cities.find(c => c.value === territory.city_id)?.label },
      address: territory.address || '',
      phone: territory.phone || '',
      email: territory.email || '',
    });
    setEditingId(territory.id);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить территорию?')) {
      try {
        await api.delete(`/settings/customer-territories/${id}/`);
        toast.success('Территория удалена');
        fetchTerritories();
      } catch (error) {
        toast.error('Ошибка удаления территории');
      }
    }
  };

  const openAddModal = () => {
    setFormData({
      customer_id: null, tb_number: '', tb_name: '', gosb_number: '', gosb_name: '',
      city_id: null, address: '', phone: '', email: ''
    });
    setEditingId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData({
      customer_id: null, tb_number: '', tb_name: '', gosb_number: '', gosb_name: '',
      city_id: null, address: '', phone: '', email: ''
    });
    setEditingId(null);
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.value === customerId);
    return customer ? customer.label : '—';
  };

  const getCityName = (cityId) => {
    const city = cities.find(c => c.value === cityId);
    return city ? city.label : '—';
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>Загрузка территорий...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h3>
          <MapPin size={24} style={{ marginRight: '0.5rem', color: '#0f766e' }} />
          Территории заказчиков
        </h3>
        <button className={styles.addButton} onClick={openAddModal}>
          <Plus size={18} />
          Добавить территорию
        </button>
      </div>

      <div className={styles.tableWrapper}>
        {territories.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>
              <MapPin size={48} />
            </div>
            <h3>Нет территорий</h3>
            <p>Добавьте первую территорию заказчика</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Заказчик</th>
                  <th>ТБ №</th>
                  <th>ТБ</th>
                  <th>ГОСБ №</th>
                  <th>ГОСБ</th>
                  <th>Город</th>
                  <th>Адрес</th>
                  <th>Контакты</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {territories.map(t => (
                  <tr key={t.id} className={styles.clickableRow} onClick={() => handleEdit(t)}>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Building size={16} style={{ color: '#0f766e' }} />
                        <strong>{getCustomerName(t.customer_id)}</strong>
                      </div>
                    </td>
                    <td>{t.tb_number || '—'}</td>
                    <td>{t.tb_name || '—'}</td>
                    <td>{t.gosb_number || '—'}</td>
                    <td>{t.gosb_name || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <MapPin size={14} style={{ color: '#64748b' }} />
                        {getCityName(t.city_id)}
                      </div>
                    </td>
                    <td style={{ maxWidth: '200px', fontSize: '0.8125rem' }}>
                      {t.address || '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        {t.phone && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8125rem' }}>
                            <Phone size={12} style={{ color: '#64748b' }} />
                            {t.phone}
                          </div>
                        )}
                        {t.email && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.8125rem' }}>
                            <Mail size={12} style={{ color: '#64748b' }} />
                            {t.email}
                          </div>
                        )}
                        {!t.phone && !t.email && '—'}
                      </div>
                    </td>
                    <td>
                      <button
                        className={styles.actionButton}
                        onClick={(event) => { event.stopPropagation(); handleDelete(t.id); }}
                        title="Удалить"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                <MapPin size={20} />
                {editingId ? 'Редактировать территорию' : 'Добавить территорию'}
              </h3>
              <button className={styles.modalCloseButton} onClick={closeModal}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.formGroup}>
                  <label>
                    <Building size={16} />
                    Заказчик
                  </label>
                  <div className={styles.select}>
                    <Select
                      options={customers}
                      value={formData.customer_id}
                      onChange={(selected) => setFormData({ ...formData, customer_id: selected })}
                      placeholder="Выберите заказчика..."
                    />
                  </div>
                </div>
                
                <div className={styles.formGroup}>
                  <label>
                    <Hash size={16} />
                    ТБ №
                  </label>
                  <input
                    type="text"
                    value={formData.tb_number}
                    onChange={(e) => setFormData({ ...formData, tb_number: e.target.value })}
                    placeholder="Введите номер ТБ"
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label>
                    <Building size={16} />
                    ТБ
                  </label>
                  <input
                    type="text"
                    value={formData.tb_name}
                    onChange={(e) => setFormData({ ...formData, tb_name: e.target.value })}
                    placeholder="Введите название ТБ"
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label>
                    <Hash size={16} />
                    ГОСБ №
                  </label>
                  <input
                    type="text"
                    value={formData.gosb_number}
                    onChange={(e) => setFormData({ ...formData, gosb_number: e.target.value })}
                    placeholder="Введите номер ГОСБ"
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label>
                    <Building size={16} />
                    ГОСБ
                  </label>
                  <input
                    type="text"
                    value={formData.gosb_name}
                    onChange={(e) => setFormData({ ...formData, gosb_name: e.target.value })}
                    placeholder="Введите название ГОСБ"
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label>
                    <MapPin size={16} />
                    Город
                  </label>
                  <div className={styles.select}>
                    <Select
                      options={cities}
                      value={formData.city_id}
                      onChange={(selected) => setFormData({ ...formData, city_id: selected })}
                      placeholder="Выберите город..."
                    />
                  </div>
                </div>
                
                <div className={styles.formGroup}>
                  <label>
                    <MapPin size={16} />
                    Адрес
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Введите адрес"
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label>
                    <Phone size={16} />
                    Телефон
                  </label>
                  <input
                    type="text"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="Введите телефон"
                  />
                </div>
                
                <div className={styles.formGroup}>
                  <label>
                    <Mail size={16} />
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Введите email"
                  />
                </div>
                
                <button type="submit" className={styles.submitButton}>
                  {editingId ? 'Обновить территорию' : 'Создать территорию'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerTerritoryList;
